/**
 * `/my-documents` — the user's uploaded files with auto-delete countdown.
 *
 * Mirrors the design's app/index.html "My documents" recreation.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { DropZone } from '../components/upload/DropZone';
import { ExpiryCountdown } from '../components/countdown/ExpiryCountdown';
import { deleteFile, getDownloadUrl, listFiles, uploadFileWithProgress } from '../api/files';
import type { FileMeta } from '@eloquor/api-contract';
import { ApiCallError } from '../api/client';

export function MyDocumentsPage() {
  const [files, setFiles] = useState<FileMeta[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const fromDemo = searchParams.get('demo') === '1';

  const refresh = useCallback(async () => {
    try {
      const { files: list } = await listFiles();
      setFiles(list);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not load files.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleUpload(file: File) {
    setError(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      await uploadFileWithProgress(file, (frac) => setUploadProgress(frac));
      await refresh();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Upload failed.');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this document? This cannot be undone.')) return;
    try {
      await deleteFile(id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not delete the document.');
    }
  }

  async function handleDownload(id: string) {
    try {
      const { url } = await getDownloadUrl(id);
      // window.open is more reliable than synthesizing an <a download> when
      // the URL targets a different origin (the S3 host). The browser uses
      // the object's Content-Disposition to trigger the download.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not start download.');
    }
  }

  return (
    <AppShell>
      {fromDemo && (
        <div className="banner">
          <strong>You're in a demo session.</strong> Uploads stay private to this session and
          auto-delete after 24 hours.
        </div>
      )}

      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div className="auth-eyebrow"><span className="num">01</span> · My documents</div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 36, margin: '4px 0 0' }}>
            Your <em>uploads</em>.
          </h1>
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg-3)', maxWidth: 280, textAlign: 'right' }}>
          Files and analyses auto-delete <strong style={{ color: 'var(--fg-1)' }}>7 days</strong> after upload.
        </div>
      </header>

      <DropZone uploading={uploading} progress={uploadProgress} onFile={handleUpload} />

      {error && (
        <div className="banner danger" style={{ marginTop: 16 }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 32 }}>
        {files === null ? (
          <div style={{ color: 'var(--fg-3)', padding: '24px 0', textAlign: 'center' }}>Loading…</div>
        ) : files.length === 0 ? (
          <div style={{ color: 'var(--fg-3)', padding: '40px 0', textAlign: 'center' }}>
            No documents yet. Upload a .docx to get started.
          </div>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 14,
            }}
          >
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <Th>Document</Th>
                <Th>Cards</Th>
                <Th>Uploaded</Th>
                <Th>Auto-deletes</Th>
                <Th style={{ width: 32 }}></Th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '14px 12px' }}>
                    <Link
                      to={`/documents/${encodeURIComponent(f.id)}`}
                      style={{ color: 'var(--ink)', fontWeight: 500, textDecoration: 'none' }}
                    >
                      {f.originalName}
                    </Link>
                    {f.warnings.length > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
                        {f.warnings.length} parser warning{f.warnings.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '14px 12px', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                    {f.cardCount}
                  </td>
                  <td style={{ padding: '14px 12px', color: 'var(--fg-3)' }}>
                    {new Date(f.uploadedAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '14px 12px' }}>
                    <ExpiryCountdown expiresAt={f.expiresAt} />
                  </td>
                  <td style={{ padding: '14px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => handleDownload(f.id)}
                      title="Download original"
                      aria-label="Download original .docx"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--fg-3)',
                        cursor: 'pointer',
                        fontSize: 16,
                        padding: 4,
                        marginRight: 4,
                      }}
                    >
                      {/* unicode "down arrow to bar" — small monoglyph keeps
                          row height stable next to the × delete glyph. */}
                      ⤓
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(f.id)}
                      title="Delete"
                      aria-label="Delete document"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--fg-3)',
                        cursor: 'pointer',
                        fontSize: 16,
                        padding: 4,
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}

function Th({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th
      style={{
        padding: '8px 12px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--fg-3)',
        ...style,
      }}
    >
      {children}
    </th>
  );
}
